import { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';

const PhotoUpload = ({ inventoryId, currentPhoto, onPhotoUpdate, isNewModel = false, onFileSelected = null, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentPhoto || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    if (isNewModel) {
      // For new models, store the file and notify parent
      setSelectedFile(file);
      if (onFileSelected) {
        onFileSelected(file);
      }
    } else {
      // Upload immediately for existing models
      await uploadPhoto(file);
    }
  };

  const uploadPhoto = async (file) => {
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const baseUrl = window.location.hostname.includes('trycloudflare.com')
        ? 'https://originally-minolta-wto-finals.trycloudflare.com'
        : '';

      const response = await fetch(`${baseUrl}/api/inventory-photos/upload/${inventoryId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setPreview(result.photo);
        onPhotoUpdate(result.photo);
        toast.success('Photo uploaded successfully!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to upload photo');
        // Revert preview on error
        setPreview(currentPhoto || '');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
      // Revert preview on error
      setPreview(currentPhoto || '');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!currentPhoto) return;

    try {
      const baseUrl = window.location.hostname.includes('trycloudflare.com')
        ? 'https://originally-minolta-wto-finals.trycloudflare.com'
        : '';

      const response = await fetch(`${baseUrl}/api/inventory-photos/${inventoryId}/photo`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setPreview('');
        onPhotoUpdate('');
        toast.success('Photo removed successfully!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to remove photo');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove photo');
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`photo-upload ${className}`}>
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Model Photo
        </label>
      </div>

      <div className="flex items-center space-x-4">
        {/* Photo Preview */}
        <div className="relative">
          <div
            className={`w-24 h-24 border-2 border-dashed rounded-lg overflow-hidden flex items-center justify-center ${
              preview ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50'
            }`}
          >
            {preview ? (
              <img
                src={preview}
                alt="Model preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center">
                <svg
                  className="mx-auto h-8 w-8 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-xs text-gray-500 mt-1">No photo</p>
              </div>
            )}
          </div>

          {/* Loading overlay */}
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            </div>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex flex-col space-y-2">
          <button
            type="button"
            onClick={triggerFileSelect}
            disabled={uploading}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? 'Uploading...' : selectedFile || preview ? 'Change Photo' : 'Select Photo'}
          </button>

          {preview && !isNewModel && (
            <button
              type="button"
              onClick={handleRemovePhoto}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
            >
              Remove
            </button>
          )}

          {isNewModel && selectedFile && (
            <div className="px-3 py-1.5 bg-green-100 text-green-800 text-sm rounded text-center">
              Photo selected - will upload after model creation
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help text */}
      <p className="text-xs text-gray-500 mt-2">
        Supported formats: JPG, PNG, GIF. Max size: 5MB. Images are automatically optimized.
      </p>
    </div>
  );
};

export default PhotoUpload;
